import React from 'react';
import type { WizardProgress } from '../utils/progress';
import type { StepId } from '../../types';
import { WizardStepHistory, type JourneyItem } from './WizardStepHistory';

interface WizardProgressBarProps {
  progress: WizardProgress;
  stepName?: string;
  userName?: string;
  stepNumber: number;
  journeyItems: JourneyItem[];
  currentStepId: StepId;
  onStepClick: (stepId: StepId) => void;
}

export const WizardProgressBar: React.FC<WizardProgressBarProps> = ({
  progress,
  stepName,
  userName,
  stepNumber,
  journeyItems,
  currentStepId,
  onStepClick,
}) => {
  const activePhase = progress.phases[progress.phaseIndex] ?? null;
  const stepCountLabel = `Trin ${stepNumber}`;
  const progressValue = Math.round(progress.progressPct);

  return (
    <div className="wizard-progress">
      <div className="wizard-progress__header">
        <div className="wizard-progress__info">
          {activePhase?.name && (
            <span className="wizard-progress__phase" aria-live="polite">
              {activePhase.name}
            </span>
          )}

          <div
            id="progress-indicator"
            className="wizard-progress__indicator"
            aria-live="polite"
          >
            <span className="wizard-progress__count">{stepCountLabel}</span>

            {stepName && (
              <span className="wizard-progress__title">{stepName}</span>
            )}
          </div>
        </div>

        {userName && (
          <div className="wizard-progress__user user-info" aria-label="Bruger">
            {userName}
          </div>
        )}
      </div>

      <div
        className="wizard-progress__bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progressValue}
        aria-valuetext={stepCountLabel}
        aria-describedby="progress-indicator"
        aria-labelledby="progress-indicator"
      >
        <div
          className="wizard-progress__bar-fill"
          style={{ width: `${progressValue}%` }}
        />
      </div>

      {progress.phases.length > 0 && (
        <div className="wizard-phases" role="list">
          {progress.phases.map((phase) => (
            <div
              key={phase.id}
              className={`wizard-phase wizard-phase--${phase.status}`}
              role="listitem"
              aria-current={phase.status === 'current' ? 'step' : undefined}
            >
              <span className="wizard-phase__dot" aria-hidden="true"></span>
              <span className="wizard-phase__label">{phase.name}</span>
            </div>
          ))}
        </div>
      )}

      <WizardStepHistory
        journeyItems={journeyItems}
        currentStepId={currentStepId}
        onStepClick={onStepClick}
      />
    </div>
  );
};
